/**
 * UI-P2A (REQ-P2A-7 / D-P2A-05/06): map an index taken from a **tier-filtered**
 * pane back to the index the pane reducer will use.
 *
 * Why this exists. `ForkPaneTooltips` receives `renderPanes`
 * (`ForkEngineSurface.tsx:252`), and `renderPanes` is
 * `filterPanesBySeriesTier(resolvedPanes, tier)` (`VNInvestCharts.tsx:917-923`) —
 * a Free-tier pane silently loses its locked series, so every index AFTER a
 * dropped series shifts down. The reducer, however, indexes the UNFILTERED
 * `panes` array (`useDynamicPanes.ts:665`, `getVisiblePanes(panes)`) and
 * `resolveSeriesIndex` prefers a numeric index **with no type check**
 * (`useDynamicPanes.ts:334-337`). Passing a filtered index through would
 * toggle/remove a different series than the one the user clicked.
 *
 * Resolution order:
 *  1. stable series `id` against the base (unfiltered) pane — exact;
 *  2. no id + arrays are the same length ⇒ nothing was dropped ⇒ the index is
 *     already correct;
 *  3. otherwise `-1` (caller must no-op). Fail closed: never act on a series the
 *     user did not click.
 */
export function resolvePaneSeriesIndex(basePanes, filteredPanes, paneId, filteredIndex, seriesId) {
    const baseSeries = basePanes.find((pane) => pane.id === paneId)?.series ?? [];
    if (seriesId) {
        const byId = baseSeries.findIndex((series) => series.id === seriesId);
        if (byId >= 0)
            return byId;
    }
    const filteredSeries = filteredPanes.find((pane) => pane.id === paneId)?.series ?? [];
    if (baseSeries.length === filteredSeries.length && filteredIndex >= 0 && filteredIndex < baseSeries.length) {
        return filteredIndex;
    }
    return -1;
}
/**
 * UI-P2A (NEW-4): the exact wrapper `ForkEngineSurface` hands to the legend.
 * Exported so the seam is testable without mounting the engine surface: the
 * legend's `(paneId, type, filteredIndex, seriesId)` is resolved to the
 * reducer index before the action runs, and unresolved rows never fire.
 */
export function createResolvedSeriesAction(basePanes, filteredPanes, action) {
    return (paneId, seriesType, filteredIndex, seriesId) => {
        const resolvedIndex = resolvePaneSeriesIndex(basePanes, filteredPanes, paneId, filteredIndex, seriesId);
        if (resolvedIndex < 0)
            return;
        action(paneId, seriesType, resolvedIndex);
    };
}
